import { FC, useRef } from 'react';
import { Button, Checkbox, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { NAMESPACE } from '@/constants';
import { COLUMN_DEFS, COLUMN_ORDER } from '../../BookingSheet.constants';
import type { BookingColumnKey } from '../../BookingSheet.types';
import { ColumnTogglePanel } from '../../BookingSheet.styles';

interface ColumnToggleProps {
  visibleColumns: Set<BookingColumnKey>;
  onChange: (next: Set<BookingColumnKey>) => void;
}

/**
 * Column visibility toggle for the 30+ possible booking fields. Lives in a Dropdown
 * so the checkbox list doesn't compete for space with the filter bar; per the styling
 * convention, `getPopupContainer` keeps the popup anchored to the trigger so clicking
 * a checkbox doesn't dismiss the panel.
 */
export const ColumnToggle: FC<ColumnToggleProps> = ({ visibleColumns, onChange }) => {
  const { t } = useTranslation(NAMESPACE.BOOKING_SHEET_PAGE);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleColumn = (key: BookingColumnKey, checked: boolean) => {
    const next = new Set(visibleColumns);
    if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }
    onChange(next);
  };

  return (
    <div ref={containerRef}>
      <Dropdown
        trigger={['click']}
        getPopupContainer={() => containerRef.current ?? document.body}
        popupRender={() => (
          <ColumnTogglePanel
            className="ant-dropdown-menu"
            role="group"
            aria-label={t('columnsToggleLabel')}
          >
            {COLUMN_ORDER.map((key) => (
              <Checkbox
                key={key}
                checked={visibleColumns.has(key)}
                onChange={(e) => toggleColumn(key, e.target.checked)}
              >
                {t(COLUMN_DEFS[key].i18nKey)}
              </Checkbox>
            ))}
          </ColumnTogglePanel>
        )}
      >
        <Button>{t('columnsToggle')}</Button>
      </Dropdown>
    </div>
  );
};

export default ColumnToggle;
